import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ZoomIn } from "lucide-react";
import { DocumentViewer } from "@/components/inbox/DocumentViewer";

const isPdf = (name, mime) => (mime || "").includes("pdf") || /\.pdf$/i.test(name || "");

function Pane({ doc }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const [mime, setMime] = useState("");
  useEffect(() => {
    let u = null;
    setUrl(null); setFailed(false);
    apiClient.get(doc.fetchUrl, { responseType: "blob" })
      .then((r) => { const t = r.headers["content-type"] || doc.mime || "image/jpeg"; setMime(t); u = URL.createObjectURL(new Blob([r.data], { type: t })); setUrl(u); })
      .catch(() => setFailed(true));
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [doc.fetchUrl, doc.mime]);
  const pdf = isPdf(doc.fileName, mime || doc.mime);
  return (
    <div className="flex min-h-0 flex-col rounded-md border border-slate-200 bg-slate-50" data-testid={`doc-compare-pane-${doc.id}`}>
      <div className={`border-b border-slate-200 px-3 py-1.5 text-sm ${doc.accent ? "bg-amber-50" : "bg-white"}`}>
        {doc.tag && <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{doc.tag}</div>}
        <div className="truncate font-medium text-slate-900" title={doc.title}>{doc.title}</div>
        {doc.subtitle && <div className="truncate text-xs text-slate-500">{doc.subtitle}</div>}
      </div>
      <div className="min-h-0 flex-1 overflow-hidden flex">
        {failed && <div className="m-auto text-sm text-slate-400">Datei nicht verfügbar</div>}
        {!failed && !url && <div className="m-auto text-sm text-slate-400">Lädt…</div>}
        {url && <DocumentViewer blobUrl={url} contentType={pdf ? "application/pdf" : (mime || doc.mime || "image/jpeg")} fileName={doc.fileName || doc.title} fitMode={doc.fitMode || "page"} />}
      </div>
    </div>
  );
}

/** Vergleichs-Lupe: zwei oder mehr Dokumente gross nebeneinander (Bilder und PDFs). */
export function DocCompareDialog({ open, onClose, title = "Dokumente gross vergleichen", docs = [], footer = null, facts = null }) {
  if (!docs.length) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-[96vw] w-[96vw] h-[94vh] flex flex-col p-4" data-testid="doc-compare-dialog">
        <DialogHeader className="flex-row items-center gap-3 space-y-0">
          <DialogTitle className="flex items-center gap-2"><ZoomIn className="h-5 w-5 text-indigo-600" /> {title}</DialogTitle>
          <DialogDescription className="sr-only">Grossansicht der Dokumente nebeneinander</DialogDescription>
        </DialogHeader>
        {facts && facts.length > 0 && (
          <div className="rounded-md border border-slate-200 overflow-hidden text-xs" data-testid="doc-compare-facts">
            <table className="w-full">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-3 py-1.5 text-left font-medium w-36">Vergleich</th>
                  {facts[0].values.map((_, i) => <th key={i} className="px-3 py-1.5 text-left font-medium">{facts[0].heads?.[i] || `Dokument ${i + 1}`}</th>)}
                  <th className="px-3 py-1.5 text-left font-medium w-28">Status</th>
                </tr>
              </thead>
              <tbody>
                {facts.map((row) => (
                  <tr key={row.label} className={`border-t border-slate-100 ${row.diff ? "bg-rose-50" : ""}`} data-testid={`doc-compare-fact-${row.key}`} data-diff={row.diff ? "true" : "false"}>
                    <td className="px-3 py-1.5 text-slate-500">{row.label}</td>
                    {row.values.map((v, i) => <td key={i} className={`px-3 py-1.5 font-mono ${row.diff ? "text-rose-700 font-semibold" : "text-slate-800"}`}>{v || "–"}</td>)}
                    <td className={`px-3 py-1.5 font-medium ${row.diff ? "text-rose-700" : "text-emerald-700"}`}>{row.diff ? "weicht ab" : "gleich"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className={`grid flex-1 min-h-0 gap-3 ${docs.length >= 4 ? "grid-cols-4" : docs.length === 3 ? "grid-cols-3" : docs.length === 2 ? "grid-cols-2" : "grid-cols-1"}`}>
          {docs.map((d) => <Pane key={d.id} doc={d} />)}
        </div>
        {footer && <DialogFooter className="pt-2">{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}
